/**
 * Family-child name resolution for AI-extracted mentions.
 *
 * The AI sometimes returns just a first name ("John") when the family
 * member is stored under a longer canonical name ("John Smith"), and
 * occasionally returns a name that could plausibly refer to more than
 * one child (siblings sharing a first name).
 *
 * Match strategy (in order):
 *   1. Case-insensitive full-string equality.
 *   2. Single-token name matches the first token of one family child's
 *      name (or vice-versa).
 *
 * Last-name-only matches ("Smith" → "John Smith") are intentionally
 * skipped because they're too ambiguous to bind without user consent.
 */

/**
 * Returns every family child that could match `name` under the rules
 * above. May be empty (no match), length 1 (unambiguous), or length 2+
 * (ambiguous — caller should disambiguate via UI).
 */
export function findFamilyChildCandidates<T extends { name: string }>(
  name: string,
  children: readonly T[],
): T[] {
  const target = name.trim().toLowerCase();
  if (!target || children.length === 0) return [];

  // Exact wins. If two family rows happen to share the exact name (a
  // user-created duplicate) we surface both so the caller can still
  // flag the ambiguity rather than silently picking the first.
  const exacts = children.filter((c) => c.name.trim().toLowerCase() === target);
  if (exacts.length > 0) return exacts;

  const targetTokens = target.split(/\s+/);
  return children.filter((c) => {
    const cTokens = c.name.trim().toLowerCase().split(/\s+/);
    if (cTokens.length === 0) return false;
    if (targetTokens.length === 1) return cTokens[0] === targetTokens[0];
    if (cTokens.length === 1) return targetTokens[0] === cTokens[0];
    return false;
  });
}

/**
 * Returns the single family child that matches `name`, or `null` when
 * there is no candidate or the match is ambiguous (2+ candidates).
 * Ambiguity is intentionally surfaced as `null` so callers fall back
 * to user-driven disambiguation instead of guessing.
 */
export function matchFamilyChild<T extends { name: string }>(
  name: string,
  children: readonly T[],
): T | null {
  const candidates = findFamilyChildCandidates(name, children);
  return candidates.length === 1 ? candidates[0] : null;
}

/**
 * Map of `ai_name.toLowerCase()` → canonical family child name, used to
 * carry the user's disambiguation choices through the review UI without
 * mutating review_items.
 */
export type DisambiguationMap = ReadonlyMap<string, string>;

/**
 * Resolves an AI-extracted name to a family child, honoring an explicit
 * user disambiguation choice when present. Falls back to
 * `matchFamilyChild` otherwise, so unambiguous extractions still bind
 * automatically.
 */
export function resolveAiChildName<T extends { name: string }>(
  aiName: string,
  family: readonly T[],
  disambiguation?: DisambiguationMap,
): T | null {
  const override = disambiguation?.get(aiName.trim().toLowerCase());
  if (override) {
    const target = override.trim().toLowerCase();
    const picked = family.find((c) => c.name.trim().toLowerCase() === target);
    if (picked) return picked;
    // The disambiguation references a child no longer in `family`
    // (deleted in another tab). Fall through to the matcher rather
    // than honoring a stale pick.
  }
  return matchFamilyChild(aiName, family);
}
